/* eslint-disable consistent-return */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import { Box, Checkbox, Loader, Typography } from '@saviynt/design-system';
import classnames from 'classnames';

import { CertsListTile } from '../../../components/CertsList';
import CertsListNoRecordsFound from '../../../components/CertsList/CertsListNoRecordsFound';
import { getTileMetaData } from '../../../components/CertsList/CertsListTile';
import {
  ACT_DISCONTINUE,
  CERTS_LIST_ALL_TAB,
} from '../../../components/CertsList/constant';
import {
  getCertListTabsCount,
  getListData,
  setConnectedTileId,
  setCurrentAction,
  setFirstListLoadFinished,
  setIsAtleastOneTileActionable,
  setIsSidePanelVisible,
  setPageReload,
  setSiblingTileId,
  setSidePanelMetaData,
} from '../../../redux/certlist/action';
import {
  LIST_PAGE_DATA_OFFSET,
  SCREEN_SIZE_MOBILE_XLARGE,
  SCREEN_WIDTH_QUERIES,
} from '../../../utils/constant';
import { getMessageKey } from '../../../utils/utils';

import CertListBodySkeleton from './CertListBodyLoader';
import { EMPTY_QUERY_LIMIT, LIMIT_CHARACTERS_FOR_QUERY } from './constant';
import { addTrackerObject } from './utils';

import './CertsListBody.css';

function CertsListBody() {
  const dispatch = useDispatch();
  const intl = useIntl();
  const {
    global: { isGlobalLoading, mediaQueryValue },
    core: {
      isAdminLogin,
      totalItems,
      isPageLoadFinished,
      isFirstListLoadFinished,
      isReloadRequired,
      recentListData,
      transitionTileId,
      currentAction,
      listOffset,
    },
    bulkAction: {
      isBulkActionEnabled,
      isBulkFooterVisible,
      isBulkChecked,
      isBulkDisabled,
      isBulkSubmittingAction,
      isBulkIndeterminate,
      isBulkSelecting,
    },
    sidePanel: {
      isSidePanelVisible,
      connectedTileId,
      siblingTileId: siblingTileInfo,
    },
    tabs: { activeTab },
    viewControls: {
      searchText,
      dynamicSortFilter: { sortByColumn, sortDirection },
      appliedRoleFilters,
    },
  } = useSelector((state) => state?.certifications?.list);
  const { messages } = useSelector((state) => state?.user);
  const { isMenuPinned } = useSelector((state) => state?.user);

  const mobileXlargeQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_MOBILE_XLARGE
  );
  const isMobileXlargeWidth = mobileXlargeQuery.value <= mediaQueryValue;

  // List manipulations
  const observerTarget = useRef(null);
  const tileListRef = useRef(null); // To check if scroll bar is present
  const firstItemRef = useRef(null); // Assignment to first from each load
  const [hasScrollbar, setHasScrollbar] = useState(false);
  const [mergedList, setMergedList] = useState([]);
  const [currentOffset, setCurrentOffset] = useState(0);

  // Booleans
  const isTotalResults = Boolean(
    (searchText?.length > LIMIT_CHARACTERS_FOR_QUERY ||
      appliedRoleFilters?.length > 0) &&
      isFirstListLoadFinished
  );
  const isStatusBarEnabled = Boolean(isBulkActionEnabled || isTotalResults);

  const certListBodyClasses = classnames(
    'CertsListBody',
    isBulkFooterVisible && 'CertsListBody--isBulkAction'
  );

  const wrapperClasses = classnames(
    'CertsListBody-wrapperContainer',
    isSidePanelVisible && 'CertsListBody-wrapperContainer--isSidePanelVisible',
    !isStatusBarEnabled && 'CertsListBody-wrapperContainer--noStatusBar',
    isMenuPinned && 'CertsListBody-wrapperContainer--isMenuPinned'
  );

  const statusBarClasses = classnames(
    'CertsList-statusBar',
    hasScrollbar && 'CertsList-statusBar--isScrollable'
  );

  const tileListClasses = classnames(
    'CertsListBody-tileList',
    isMenuPinned && 'CertsListBody-tileList--isMenuPinned'
  );

  /**
   * useEffect to merge the new set of list data to existing.
   *
   * This effect runs whenever the `recentListData` dependency changes.
   * If `recentListData` is changed, the `addTrackerObject` function is called
   * to add tracker object to every array element.
   * Then the records are appended to previous results forming a continuous list.
   *
   * Dependencies:
   * - `recentListData`: An array which contains latest fetched data from the api.
   */
  useEffect(() => {
    if (recentListData) {
      const recentListDataWithTracker = addTrackerObject(recentListData);

      dispatch(
        setIsAtleastOneTileActionable(
          recentListDataWithTracker.some(
            (node) => node?.tracker?.actions?.length > 0
          )
        )
      );

      if (Number(listOffset) === 0) {
        setMergedList(recentListDataWithTracker);
      } else {
        setMergedList((prev) => [...prev, ...recentListDataWithTracker]);
      }
    }
  }, [recentListData]);

  /**
   * Updates the sibling tile metadata and modifies the connection state of tracker nodes in an array.
   *
   * @param {Array} arr - An array of objects representing nodes. Each node is expected to have a `tracker` object with an `id` property.
   * @returns {Array} A new array with updated `isConnected` states for the `tracker` objects of the nodes.
   *
   * The function performs the following:
   * 1. Identifies the index of the node whose `tracker.id` matches the global `connectedTileId`.
   * 2. Determines the metadata for the sibling tiles (previous and next) based on the current node's position.
   *    - If a sibling exists, its metadata is retrieved using `getTileMetaData`.
   *    - If no sibling exists (e.g., for the first or last node), the corresponding sibling metadata is set to `null`.
   * 3. Dispatches the sibling tile IDs (`prev` and `next`) to the store using the `setSiblingTileId` action.
   * 4. Returns a new array where each node's `tracker.isConnected` property is updated based on whether its `tracker.id`
   *    matches the global `connectedTileId`.
   *
   * External dependencies:
   * - `connectedTileId` (global variable): The ID of the currently connected tile.
   * - `isAdminLogin` (global variable): A flag indicating the user's admin status.
   * - `getTileMetaData(node, isAdminLogin)`: A function that retrieves metadata for a given node.
   * - `dispatch(action)`: A function that dispatches an action to update the state.
   * - `setSiblingTileId(prev, next)`: An action creator for setting sibling tile IDs in the state.
   */
  const handleSidePanelSwitch = (arr) => {
    const siblingTileId = {};
    const targetIndex = arr?.findIndex(
      (node) => node?.tracker?.id === connectedTileId
    );

    siblingTileId.prev =
      targetIndex > 0
        ? getTileMetaData(messages, intl, arr[targetIndex - 1], isAdminLogin)
        : null;
    siblingTileId.next =
      targetIndex < arr.length - 1
        ? getTileMetaData(messages, intl, arr[targetIndex + 1], isAdminLogin)
        : null;

    dispatch(setSiblingTileId(siblingTileId.prev, siblingTileId.next));

    return arr.map((record) => ({
      ...record,
      tracker: {
        ...record?.tracker,
        isConnected: connectedTileId === record?.tracker?.id,
      },
    }));
  };

  /**
   * Effect hook to manage the `isConnected` state and sibling tile IDs in response to changes in `connectedTileId`.
   *
   * @param {Array} mergedList - A list of objects, each containing a `tracker` object with an `isConnected` property.
   * @param {string|null} connectedTileId - The ID of the currently connected tile. If null, no tile is connected.
   * @param {Function} setMergedList - A state updater function for updating the `mergedList` state.
   * @param {Function} dispatch - A function to dispatch actions to the store.
   * @param {Function} handleSidePanelSwitch - A function to process the list and update sibling tile metadata and connection states.
   * @param {Function} setSiblingTileId - An action creator to update the sibling tile IDs in the state.
   *
   * Dependencies: [connectedTileId]
   *
   * The effect performs the following actions:
   * 1. If `connectedTileId` is defined:
   *    - Creates a copy of the `mergedList` and processes it using `handleSidePanelSwitch` to:
   *      - Update the sibling tile metadata.
   *      - Update the `isConnected` property of each node's `tracker` based on `connectedTileId`.
   *    - Updates the `mergedList` state with the processed list.
   *    - Exits early (returns).
   * 2. If `connectedTileId` is not defined:
   *    - Checks whether all `tracker.isConnected` properties in `mergedList` are `false` using `.every()`.
   *    - If any `tracker.isConnected` is still `true`:
   *      - Updates `mergedList` to reset all `tracker.isConnected` properties to `false`.
   *      - Dispatches `setSiblingTileId` with `null` values for `prev` and `next` to reset sibling tile metadata in the state.
   */
  useEffect(() => {
    if (connectedTileId) {
      const tempMergedList = [...mergedList];
      const modifiedTempMergedList = handleSidePanelSwitch(tempMergedList);

      setMergedList(modifiedTempMergedList);

      return;
    }

    const isEveryIsConnectedClosed = mergedList.every(
      (record) => !record?.tracker?.isConnected
    );

    if (!isEveryIsConnectedClosed) {
      setMergedList((prev) =>
        prev.map((record) => ({
          ...record,
          tracker: {
            ...record.tracker,
            isConnected: false,
          },
        }))
      );

      dispatch(setSiblingTileId(null, null));
    }
  }, [connectedTileId]);

  /**
   * Effect hook to handle the transition and side panel state updates based on `transitionTileId`.
   *
   * @param {string|null} transitionTileId - The ID of the tile in transition. If null, no tile is transitioning.
   * @param {boolean} isSidePanelVisible - A flag indicating whether the side panel is currently visible.
   * @param {string|null} connectedTileId - The ID of the currently connected tile. If null, no tile is connected.
   * @param {object} siblingTileInfo - Information about sibling tiles, containing metadata for the `next` tile.
   * @param {Function} setMergedList - State updater function to update the `mergedList` state.
   * @param {Function} dispatch - Function to dispatch actions to the store.
   * @param {Function} setConnectedTileId - Action creator to update the `connectedTileId` in the state.
   * @param {Function} setSidePanelMetaData - Action creator to update the side panel metadata in the state.
   * @param {Function} setIsSidePanelVisible - Action creator to toggle the visibility of the side panel.
   * @param {Array} mergedList - A list of objects, each containing a `tracker` object with various state properties.
   *
   * Dependencies: [transitionTileId]
   *
   * The effect performs the following actions:
   * 1. Checks if `transitionTileId` is defined:
   *    - If the side panel is visible, the `connectedTileId` does not match `siblingTileInfo.next.id`, and a valid `siblingTileInfo.next` exists:
   *      - Dispatches `setConnectedTileId` to update the connected tile to the next sibling.
   *      - Dispatches `setSidePanelMetaData` to update the metadata for the next sibling tile.
   *    - Otherwise, hides the side panel by dispatching `setIsSidePanelVisible(false)`.
   * 2. Updates the `mergedList` state to set `tracker.isTransition` to `true` for the tile with the `transitionTileId` or tiles already in transition.
   * 3. Sets a timeout of 500ms to:
   *    - Remove the transitioning tile (`transitionTileId`) from the `mergedList`.
   *    - Update the `tracker.isConnected` property for all remaining tiles based on `connectedTileId`.
   *    - Updates the `mergedList` state with the processed list.
   */
  useEffect(() => {
    if (transitionTileId) {
      if (
        isSidePanelVisible &&
        connectedTileId !== siblingTileInfo?.next?.id &&
        Boolean(siblingTileInfo?.next)
      ) {
        dispatch(setConnectedTileId(siblingTileInfo?.next?.id));
        dispatch(setSidePanelMetaData(siblingTileInfo?.next));
      } else {
        dispatch(setIsSidePanelVisible(false));
      }

      if (activeTab !== CERTS_LIST_ALL_TAB) {
        setMergedList((prev) =>
          prev?.map((record) => ({
            ...record,
            tracker: {
              ...record?.tracker,
              isTransition:
                record?.tracker?.id === transitionTileId ||
                record?.tracker?.isTransition,
            },
          }))
        );

        setTimeout(() => {
          const tempMergedList = mergedList
            .filter((record) => record?.tracker?.id !== transitionTileId)
            .map((record) => ({
              ...record,
              tracker: {
                ...record?.tracker,
                isConnected: connectedTileId === record?.tracker?.id,
              },
            }));

          setMergedList(tempMergedList);
          // updates the tab counts
          dispatch(getCertListTabsCount());
        }, 500);

        return;
      }

      if (currentAction === ACT_DISCONTINUE) {
        setMergedList((prev) =>
          prev?.map((node) => {
            if (node?.tracker?.id === transitionTileId) {
              return {
                ...node,
                statusLabel: 'discontinued',
                tracker: {
                  ...node?.tracker,
                  actions: node?.tracker?.actions?.filter(
                    (action) => action !== ACT_DISCONTINUE
                  ),
                },
              };
            }

            return node;
          })
        );

        dispatch(setCurrentAction(null));
      }
    }
  }, [transitionTileId]);

  useEffect(() => {
    if (isFirstListLoadFinished) {
      // Check if the scrollHeight is greater than the client Height i.e if scrollbar is present
      if (tileListRef.current) {
        setHasScrollbar(
          tileListRef.current.scrollHeight > tileListRef.current.clientHeight
        );
      }

      // Place the first item from each batch to center except for the first load
      if (
        firstItemRef &&
        firstItemRef.current &&
        firstItemRef.current instanceof HTMLElement
      ) {
        firstItemRef.current.scrollIntoView({
          block: 'center',
        });
      }
    }
  }, [mergedList, isFirstListLoadFinished]);

  /**
   * This effect hook is responsible for fetching and loading more list items as the user scrolls.
   * It uses the Intersection Observer API to detect when the list container is near the viewport's bottom.
   * When the conditions are met, it triggers an list data API call to fetch more data and updates the list state.
   *
   * @param {Object} observerTarget - A reference to the DOM element that will be observed for intersection.
   * @param {boolean} isGlobalLoading - A flag indicating whether the global loading state is active.
   * @param {boolean} isPageLoadFinished - A flag indicating whether the page loading is finished.
   * @param {boolean} isFirstListLoadFinished - A flag indicating whether the first list data loading is finished.
   * @param {number} currentOffset - The current offset for the list data API call.
   * @param {function} setCurrentOffset - A function to update the current offset for the list data API call.
   *
   * @returns {void}
   */
  useEffect(() => {
    if (!isPageLoadFinished || isGlobalLoading) return;

    const observer = new IntersectionObserver(
      (elements) => {
        // Weather to hit list data API or not
        const shouldRunListDataApi = Boolean(
          elements[0].isIntersecting &&
            (!isFirstListLoadFinished || currentOffset < totalItems)
        );

        if (shouldRunListDataApi) {
          dispatch(
            getListData({
              offset: String(currentOffset),
              max: String(LIST_PAGE_DATA_OFFSET),
            })
          );
          setCurrentOffset((prevOffset) => prevOffset + LIST_PAGE_DATA_OFFSET);
        }
      },
      { threshold: isMobileXlargeWidth ? 0.4 : 0.8 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }

      return undefined;
    };
  }, [
    observerTarget,
    isGlobalLoading,
    isPageLoadFinished,
    isFirstListLoadFinished,
  ]);

  /**
   * Handles the reloading of the certification list.
   *
   * This function is memoized using `useCallback` and performs the necessary steps to reset
   * the certification list state and prepare for a fresh API call. It clears the current list,
   * resets the offset and API call count, and updates relevant Redux states to indicate
   * that a reload is in progress.
   *
   * @function handleListReload
   * @returns {void}
   *
   * @description
   * - Clears the merged list of items by resetting it to an empty array.
   * - Resets the current offset for fetching the list data back to 0.
   * - Resets the remaining API call count to its initial value (1).
   * - Dispatches a Redux action to mark the first list data loading as unfinished.
   * - Dispatches a Redux action to update the page reload state to `false`.
   *
   * @dependencies
   * - `setMergedList`: State setter to clear the merged list.
   * - `setCurrentOffset`: State setter to reset the API call offset.
   * - `dispatch`: Redux dispatch function to trigger state updates.
   * - `setFirstListLoadFinished`: Redux action to reset the first list load state.
   * - `setPageReload`: Redux action to update the page reload state.
   */
  const handleListReload = useCallback(() => {
    setMergedList([]);
    setCurrentOffset(0);
    dispatch(setIsSidePanelVisible(false));
    dispatch(setFirstListLoadFinished(false));
    dispatch(setPageReload(false));
  }, []);

  /**
   * useEffect to trigger the list reload when `isReloadRequired` is true.
   *
   * This effect runs whenever the `isReloadRequired` dependency changes.
   * If `isReloadRequired` is true, the `handleListReload` function is called
   * to reload the list data or perform a similar operation.
   *
   * Dependencies:
   * - `isReloadRequired`: A boolean flag that determines if the reload is needed.
   */
  useEffect(() => {
    if (isReloadRequired) handleListReload();
  }, [isReloadRequired]);

  /**
   * useEffect to dispatch a page reload when `activeTab` changes and the first list load is finished.
   *
   * This effect runs when either `activeTab` or `isFirstListLoadFinished` changes.
   * If `activeTab` is set and the first list load has completed, it dispatches
   * an action to trigger a page reload (e.g., `setPageReload(true)`).
   *
   * Dependencies:
   * - `activeTab`: Represents the currently active tab.
   * - `isFirstListLoadFinished`: A flag indicating that the initial list load is complete.
   *
   * Dispatch:
   * - Calls `dispatch(setPageReload(true))` to notify that a page reload is required.
   */
  useEffect(() => {
    if (activeTab && isFirstListLoadFinished) dispatch(setPageReload(true));
  }, [activeTab]);

  /**
   * useEffect to dispatch a page reload when `searchText` changes to filter the records.
   *
   * This effect runs when either `searchText` changes.
   * If `searchText` is set , it dispatches
   * If `sortByColumn` is set, it dispatches
   * If `sortDirection` is set, it dispatches
   * If `appliedRoleFilters` is set, it dispatches
   * an action to trigger a page reload (e.g., `setPageReload(true)`).
   *
   * Dependencies:
   * - `searchText`: Represents the search text to filter records.
   * - `sortByColumn`: Represents on what column type to filter records.
   * - `sortDirection`: Represents the asc/desc order to filter records.
   * - `appliedRoleFilters`: Represents role or Actve/Inactive status records.
   * Dispatch:
   * - Calls `dispatch(setPageReload(true))` to notify that a page reload is required.
   */
  useEffect(() => {
    if (
      searchText.length > LIMIT_CHARACTERS_FOR_QUERY ||
      searchText.length === EMPTY_QUERY_LIMIT ||
      (sortByColumn && sortDirection) ||
      (appliedRoleFilters && isFirstListLoadFinished)
    ) {
      dispatch(setPageReload(true));
    }
  }, [searchText, sortByColumn, sortDirection, appliedRoleFilters]);

  /**
   * Renders the body of the certification list.
   *
   * This function is memoized using `useCallback` and determines the content to display
   * based on the state of the data. It checks whether the certification list data is available
   * and renders the appropriate UI:
   * - Displays the list of items if data is available.
   * - Displays a message with context of "No certificates found" if the data is empty.
   * - Returns `null` if the initial data loading is incomplete.
   *
   * @callback renderCertListBody
   * @returns {JSX.Element|null} - The rendered certification list body:
   *   - A list of certification items if data exists.
   *   - A fallback similar to "No certificates found" message if the list is empty.
   *   - `null` if the initial list loading has not finished.
   *
   * @dependencies
   * - `totalItems`: The total number of certification items available.
   * - `mergedList`: The merged list of certification items to display.
   * - `isFirstListLoadFinished`: A flag indicating whether the initial list data has been loaded.
   */
  const renderCertListBody = useCallback(() => {
    const isListDataAvailable = Boolean(
      totalItems > 0 && mergedList.length > 0
    );

    if (!isFirstListLoadFinished) return null;

    if (isListDataAvailable) {
      return mergedList.map((certificationData, index) => {
        const listLength = mergedList.length;
        const lastDivisibleIndex = listLength - LIST_PAGE_DATA_OFFSET;

        return (
          <CertsListTile
            tileRef={
              index > LIST_PAGE_DATA_OFFSET - 1 && lastDivisibleIndex === index
                ? firstItemRef
                : null
            }
            key={certificationData?.certKey}
            certData={certificationData}
          />
        );
      });
    }

    return <CertsListNoRecordsFound />;
  }, [totalItems, mergedList]);

  /**
   * Renders a loading skeleton or filler for the certificate list based on the loading state.
   *
   * This function determines whether to display a skeleton component or a filler div
   * for the certificate list UI, depending on whether the list is fully loaded or in a
   * global loading state.
   *
   * @function renderCertListLoadingElement
   * @returns {JSX.Element | null} The JSX for the loading skeleton or filler div, or `null`
   *                               if the list is already fully loaded.
   *
   * Logic:
   * - If the certificate list (`mergedList`) is fully loaded (i.e., its length equals `totalItems`),
   *   return `null` to indicate no loading UI is needed.
   * - If `isGlobalLoading` is `true`, return the `CertListBodySkeleton` component.
   * - Otherwise, return a `div` with the class `CertListBodyLoader-filler`.
   *
   * Dependencies:
   * - `mergedList`: An array representing the merged certificate list.
   * - `totalItems`: The total number of items expected in the list.
   * - `isGlobalLoading`: A boolean indicating whether the application is in a global loading state.
   */
  const renderCertListLoadingElement = () => {
    const isListLoadedOnUI = Boolean(
      mergedList?.length === totalItems &&
        (totalItems !== 0 || mergedList?.length !== 0)
    );

    const fillerClasses = isMobileXlargeWidth
      ? 'CertListBodyLoader-filler--isMobile'
      : 'CertListBodyLoader-filler';

    if (isListLoadedOnUI) return <div className={fillerClasses} />;

    if (isGlobalLoading) return <CertListBodySkeleton />;

    return <div className={fillerClasses} />;
  };

  return (
    <div className={certListBodyClasses} data-testid='certs-list-body'>
      <Box className={wrapperClasses}>
        {isStatusBarEnabled && (
          <div className={statusBarClasses}>
            {isBulkActionEnabled && (
              <div className='CertList-statusBarSelectAllCheckbox'>
                <Checkbox
                  id='simple-text-certs-list-body-select-all-checkbox'
                  name='simpleText'
                  isChecked={isBulkChecked}
                  isDisabled={
                    totalItems === 0 ||
                    isBulkDisabled ||
                    isBulkSubmittingAction ||
                    isGlobalLoading
                  }
                  isIndeterminate={isBulkIndeterminate}
                  onChange={() => {}}
                  label={`${
                    messages.CERTS_LIST_SELECT_ALL ||
                    localize(intl, getMessageKey('CERTS_LIST_SELECT_ALL'))
                  } (${totalItems})`}
                  textPosition='after'
                  showHoverEffect
                  dataTestId='simpleText'
                />
                {isBulkSelecting && (
                  <Loader
                    kind='loop'
                    format='inline'
                    color='brand'
                    size='small'
                  />
                )}
              </div>
            )}
            {isTotalResults && (
              <div className='CertsList-statusBarTotalResults'>
                <Typography kind='body2' desktop='body2-d' mobile='body2-d'>
                  {`${
                    messages.CERTS_TOTAL_RESULTS ||
                    localize(intl, getMessageKey('CERTS_TOTAL_RESULTS'))
                  }: ${totalItems}`}
                </Typography>
              </div>
            )}
          </div>
        )}
        <div className={tileListClasses} ref={tileListRef}>
          {renderCertListBody()}
          <div className='CertListBodyLoader' ref={observerTarget}>
            {renderCertListLoadingElement()}
          </div>
        </div>
      </Box>
    </div>
  );
}

export default CertsListBody;
